import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Chip,
  Alert,
  TablePagination,
} from '@mui/material';
import { gamesAPI } from '../../Api';
import PageHeader from '../../components/PageHeader';

export default function GamesHistory() {
  const [rows, setRows] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [earnings, setEarnings] = useState({ totalEarned: 0, loseCount: 0, byGame: [] });
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    userId: '',
    gameId: '',
    result: '',
    fromDate: '',
    toDate: '',
  });

  const loadGames = async () => {
    try {
      const res = await gamesAPI.list();
      setGames(res.data?.data || []);
    } catch (_) {}
  };

  const fetchHistory = async (targetPage = page, targetLimit = rowsPerPage) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: targetPage + 1,
        limit: targetLimit,
      };

      if (filters.userId) params.userId = filters.userId;
      if (filters.gameId) params.gameId = filters.gameId;
      if (filters.result) params.result = filters.result;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;

      const res = await gamesAPI.history(params);
      setRows(res.data?.data || []);
      setTotal(Number(res.data?.pagination?.total || 0));
    } catch (e) {
      setError(e.response?.data?.message || 'Ошибка загрузки истории');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchEarnings = async () => {
    setEarningsLoading(true);
    try {
      const params = {};
      if (filters.gameId) params.gameId = filters.gameId;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;

      const res = await gamesAPI.earnings(params);
      setEarnings(res.data?.data || { totalEarned: 0, loseCount: 0, byGame: [] });
    } catch (_) {
      setEarnings({ totalEarned: 0, loseCount: 0, byGame: [] });
    } finally {
      setEarningsLoading(false);
    }
  };

  useEffect(() => {
    loadGames();
    fetchHistory(0, rowsPerPage);
    fetchEarnings();
  }, []);

  const onFilterChange = (field) => (e) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const applyFilters = () => {
    setPage(0);
    fetchHistory(0, rowsPerPage);
    fetchEarnings();
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    fetchHistory(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (e) => {
    const next = parseInt(e.target.value, 10);
    setRowsPerPage(next);
    setPage(0);
    fetchHistory(0, next);
  };

  return (
    <Box>
      <PageHeader title="История игр" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Фильтры</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Telegram userId"
                value={filters.userId}
                onChange={onFilterChange('userId')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Игра"
                value={filters.gameId}
                onChange={onFilterChange('gameId')}
              >
                <MenuItem value="">Все</MenuItem>
                {games.map((g) => (
                  <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Результат"
                value={filters.result}
                onChange={onFilterChange('result')}
              >
                <MenuItem value="">Все</MenuItem>
                <MenuItem value="win">win</MenuItem>
                <MenuItem value="lose">lose</MenuItem>
                <MenuItem value="refund">refund</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="От"
                type="date"
                value={filters.fromDate}
                onChange={onFilterChange('fromDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="До"
                type="date"
                value={filters.toDate}
                onChange={onFilterChange('toDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={applyFilters} disabled={loading}>
                Применить
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Заработок игр за период (по проигрышам)</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Всего заработано: {Number(earnings.totalEarned || 0).toLocaleString('ru-RU')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Кол-во проигрышей: {Number(earnings.loseCount || 0).toLocaleString('ru-RU')}
          </Typography>
          {earningsLoading ? (
            <Typography variant="body2">Загрузка...</Typography>
          ) : (
            (earnings.byGame || []).slice(0, 10).map((item) => (
              <Typography key={item.gameId} variant="body2">
                {(item.gameName || item.gameSlug || item.gameId)}: {Number(item.totalEarned || 0).toLocaleString('ru-RU')} ({item.loseCount} проигрышей)
              </Typography>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Записи</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Дата</TableCell>
                  <TableCell>Юзер</TableCell>
                  <TableCell>Игра</TableCell>
                  <TableCell>Инициатор</TableCell>
                  <TableCell>Ставка</TableCell>
                  <TableCell>Выигрыш</TableCell>
                  <TableCell>Результат</TableCell>
                  <TableCell>Баланс юзера до/после</TableCell>
                  <TableCell>Баланс игры до/после</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row._id}>
                    <TableCell>{new Date(row.created_at).toLocaleString('ru-RU')}</TableCell>
                    <TableCell>{row.tgUser?.userId || '—'}</TableCell>
                    <TableCell>{row.game?.name || '—'}</TableCell>
                    <TableCell>{row.merchantInitiator?.name || '—'}</TableCell>
                    <TableCell>{Number(row.betAmount || 0).toLocaleString('ru-RU')}</TableCell>
                    <TableCell>{Number(row.winAmount || 0).toLocaleString('ru-RU')}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.result}
                        color={row.result === 'win' ? 'success' : row.result === 'lose' ? 'error' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {Number(row.userBalanceBefore || 0).toLocaleString('ru-RU')} → {Number(row.userBalanceAfter || 0).toLocaleString('ru-RU')}
                    </TableCell>
                    <TableCell>
                      {Number(row.gameBalanceBefore || 0).toLocaleString('ru-RU')} → {Number(row.gameBalanceAfter || 0).toLocaleString('ru-RU')}
                    </TableCell>
                  </TableRow>
                ))}
                {!rows.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">Нет данных</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
